import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import { aiChat, AiProposal } from '../../service/ai/ai-service';
import AiMessageContent from '../menu/ai/AiMessageContent';
import AiProposalCard from '../menu/ai/AiProposalCard';
import './AiAssistantPanel.css';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    error?: boolean;
    /** Action proposals of this turn — cards rendered from server data. */
    proposals?: AiProposal[];
}

interface Props {
    businessId: number;
    /** Question typed in the header bar that opened the panel (sent immediately). */
    initialQuestion?: string;
    /** Label of the admin screen behind the panel — lets "explica essa tela" work. */
    screenLabel?: string;
    onClose: () => void;
}

/**
 * The AI assistant ("secretária virtual") chat panel, anchored under the header
 * bar. Keeps one conversation per mount; the backend enforces that the answers
 * only ever come from the selected business's data.
 */
export default function AiAssistantPanel({ businessId, initialQuestion, screenLabel, onClose }: Props) {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const conversationIdRef = useRef<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentInitial = useRef(false);

    const send = async (text: string) => {
        const question = text.trim();
        if (!question || busy) return;
        setMessages((prev) => [...prev, { role: 'user', content: question }]);
        setInput('');
        setBusy(true);
        try {
            const token = await getAccessTokenSilently();
            const result = await aiChat(businessId, question, conversationIdRef.current, token, screenLabel);
            conversationIdRef.current = result.conversationId;
            setMessages((prev) => [...prev,
                { role: 'assistant', content: result.reply, proposals: result.proposals }]);
        } catch (e) {
            const raw = e instanceof Error ? e.message : '';
            // Backend messages are already user-friendly (rate limit, not configured);
            // anything else gets the generic error.
            const friendly = raw && !raw.startsWith('ai-service') && !raw.includes('Unauthorized')
                ? raw : t('ai.errorGeneric');
            setMessages((prev) => [...prev, { role: 'assistant', content: friendly, error: true }]);
        } finally {
            setBusy(false);
        }
    };

    // Fire the question that opened the panel exactly once.
    useEffect(() => {
        if (initialQuestion && !sentInitial.current) {
            sentInitial.current = true;
            void send(initialQuestion);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuestion]);

    // Long replies: land at the TOP of the assistant's answer so the user reads
    // from the beginning (scrolling to the bottom forced them to scroll back up).
    // While typing/sending, keep following the bottom.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const last = messages[messages.length - 1];
        if (!busy && last && last.role === 'assistant') {
            const nodes = el.querySelectorAll<HTMLElement>('.ai-msg');
            const target = nodes[nodes.length - 1];
            if (target) {
                const top = target.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
                el.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
                return;
            }
        }
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, busy]);

    // Portal to <body> — like the app's drawers — so the header's transform/overflow
    // can't trap or clip the fixed-position panel.
    return createPortal(
        <div className="ai-panel" data-cy="ai-panel">
            <div className="ai-panel__head">
                <span className="ai-panel__title">
                    {t('ai.panelTitle')} <span className="gs-aibar__badge">{t('ai.badge')}</span>
                </span>
                <button type="button" className="ai-panel__close" onClick={onClose}
                        aria-label={t('ai.close')} data-cy="ai-panel-close">
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            <div className="ai-panel__body" ref={scrollRef}>
                {messages.length === 0 && !busy && (
                    <div className="ai-panel__empty">{t('ai.emptyHint')}</div>
                )}
                {messages.map((m, i) => (
                    <div key={i}
                         className={`ai-msg ai-msg--${m.role}${m.error ? ' ai-msg--error' : ''}`}
                         data-cy={`ai-msg-${m.role}`}>
                        {m.role === 'assistant' && !m.error
                            ? <AiMessageContent content={m.content} />
                            : m.content}
                        {m.proposals?.map((p) => (
                            <AiProposalCard key={p.id} businessId={businessId} proposal={p} />
                        ))}
                    </div>
                ))}
                {busy && (
                    <div className="ai-msg ai-msg--assistant ai-msg--typing" aria-label={t('ai.thinking')}>
                        <span /><span /><span />
                    </div>
                )}
            </div>

            <form
                className="ai-panel__inputrow"
                onSubmit={(e) => { e.preventDefault(); void send(input); }}
            >
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t('ai.inputPlaceholder')}
                    disabled={busy}
                    autoFocus
                    data-cy="ai-panel-input"
                />
                <button type="submit" disabled={busy || !input.trim()}
                        aria-label={t('ai.send')} data-cy="ai-panel-send">
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </form>
        </div>,
        document.body,
    );
}
